import React, { useEffect, useState, useContext } from 'react';
import i18next from 'core/i18n';
import { TinyTitle, palettes, useFlashMessage, Modal as ModalRoot, LargeTitle } from '@indigo-ag/component-module';
import {
  AgFinancingFilesContext,
  AgFinancingDocumentType,
  AgFinancingFileStatus,
  AgFinancingFile,
  AgFinancingMatriculaInput
} from '@indigo-ag/schema';
import { useMutation } from '@apollo/client';

import { UploadDocument, createStylesHook } from 'shared';
import { FormattedMatriculasDetailData, MatriculaContext } from '../MatriculasList';
import { ProgressDictionary, removeFilesFromAgFinancing } from '../../../../utils';
import {
  updateAgFinancingMatricula,
  getAgFinancingMatriculas,
  createAgFinancingFile,
  getDocumentsQueryByFinancingId
} from 'features/brazil-grower/queries';

import styled from 'styled-components';

interface UploadFileModalProps {
  closeModal: () => void;
  open: boolean;
  isFieldMonitoring?: boolean;
  selectedMatricula?: FormattedMatriculasDetailData;
}

const useStyles = createStylesHook({
  titleStyles: {
    color: `${palettes.brand.gray['700']} !important`,
    padding: '5px 0px 5px'
  }
});

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 28px;
`;

const Modal = styled(ModalRoot)`
  width: 100%;
  max-width: 680px;
`;

export const UploadFileModal: React.FunctionComponent<UploadFileModalProps> = ({
  closeModal,
  open,
  isFieldMonitoring,
  selectedMatricula
}) => {
  /**
   * State
   */
  const [files, setFiles] = useState<File[] | null>(null);
  const [progress, setProgress] = useState<ProgressDictionary>();

  /**
   * MatriculaContext
   */
  const { uploadFilesToPhase, agFinancingId } = useContext(MatriculaContext);

  /**
   * Mutations
   */
  const [updateAgFinancingMatriculaFn] = useMutation(updateAgFinancingMatricula);
  const [createAgFinancingFileFn] = useMutation(createAgFinancingFile);

  useEffect(() => {
    setFiles(null);
  }, [open]);

  /**
   * Styles
   */
  const classes = useStyles();

  const updateFiles = (files: File[]) => {
    setFiles(files);
  };

  const { addFlashMessage } = useFlashMessage();

  const handleSave = () => {
    if (files) {
      const currentKML = !isFieldMonitoring ? selectedMatricula?.kml : undefined;
      return uploadFilesToPhase(
        files,
        AgFinancingFilesContext.DOCUMENTS,
        !isFieldMonitoring ? AgFinancingDocumentType.KML : AgFinancingDocumentType.FIELD_MONITORING_REPORT,
        percentCompleted => setProgress({ ...percentCompleted })
      )
        .then(filesUploaded => {
          const fulfilled = filesUploaded.fulfilled;
          if (fulfilled.length) {
            return createAgFinancingFileFn({
              refetchQueries: [
                {
                  ...getDocumentsQueryByFinancingId(agFinancingId)
                }
              ],
              variables: {
                data: fulfilled.map(file => ({
                  ...file,
                  agFinancingId,
                  status: AgFinancingFileStatus.SENT
                }))
              }
            });
          }
          return Promise.reject('Unable to upload files to AWS right now');
        })
        .then(({ data }: { data?: { createAgFinancingFile: AgFinancingFile[] } }) => {
          if (data && data.createAgFinancingFile && data.createAgFinancingFile.length) {
            const matricula: AgFinancingMatriculaInput = {
              id: selectedMatricula?.id,
              kml: !isFieldMonitoring ? { id: data.createAgFinancingFile[0].id } : undefined,
              fieldMonitoringFiles: isFieldMonitoring
                ? data.createAgFinancingFile.map(file => ({ id: file.id }))
                : undefined
            };

            return updateAgFinancingMatriculaFn({
              refetchQueries: [{ query: getAgFinancingMatriculas, variables: { agFinancingId } }],
              variables: {
                agFinancingId,
                matricula
              }
            });
          }
          return Promise.reject('Unable to link new files to this agFinanceProcess');
        })
        .then(() => {
          // We need to remove the previous file if there is any
          if (currentKML) {
            return removeFilesFromAgFinancing([currentKML.id]);
          }
          return Promise.resolve();
        })
        .then(() => {
          addFlashMessage({
            content: i18next.t('fileUploadedSuccessfully', { count: files.length }),
            title: '',
            variant: 'success'
          });
        })
        .catch(() => {
          addFlashMessage({
            content: i18next.t('error'),
            title: '',
            variant: 'error'
          });
        })
        .finally(() => {
          setFiles(null);
          setProgress({});
        });
    }
    return Promise.resolve();
  };

  return (
    <Modal
      title={selectedMatricula?.matricula?.documentName ?? ''}
      open={open}
      onRequestClose={closeModal}
      renderHeader={({ TitleText, CloseButton }) => (
        <HeaderContainer>
          <LargeTitle>{selectedMatricula?.matricula?.documentName}</LargeTitle>
          <CloseButton />
        </HeaderContainer>
      )}
      renderContent={() => (
        <>
          <TinyTitle className={classes.titleStyles}>
            {!isFieldMonitoring ? i18next.t('uploadKmlFile') : i18next.t('georeferencingReportFiles')}
          </TinyTitle>
          <UploadDocument files={files || []} setFiles={updateFiles} progress={progress} />
        </>
      )}
      actionButtons={[
        {
          onClick: closeModal,
          label: i18next.t('cancel'),
          variant: 'secondary'
        },
        {
          onClick: handleSave,
          disabled: !files,
          label: i18next.t('saveChanges'),
          variant: 'primary'
        }
      ]}
    />
  );
};
